import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getCurrentUser = () => api.get('/auth/me');
export const updateMyProfile = (data) => api.put('/auth/me', data);

// Customers
export const getCustomers = (search) =>
  api.get('/customers', { params: { search } });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);

// Contacts
export const getContacts = (customerId) =>
  api.get(`/customers/${customerId}/contacts`);
export const createContact = (customerId, data) =>
  api.post(`/customers/${customerId}/contacts`, data);
export const updateContact = (customerId, id, data) =>
  api.put(`/customers/${customerId}/contacts/${id}`, data);
export const deleteContact = (customerId, id) =>
  api.delete(`/customers/${customerId}/contacts/${id}`);

// Contact Info Templates
export const getContactInfoTemplates = () =>
  api.get('/contact-info-templates');
export const getContactInfoTemplate = (id) =>
  api.get(`/contact-info-templates/${id}`);
export const createContactInfoTemplate = (data) =>
  api.post('/contact-info-templates', data);
export const updateContactInfoTemplate = (id, data) =>
  api.put(`/contact-info-templates/${id}`, data);
export const deleteContactInfoTemplate = (id) =>
  api.delete(`/contact-info-templates/${id}`);

// Interactions
export const getInteractions = (customerId) =>
  api.get(`/customers/${customerId}/interactions`);
export const createInteraction = (customerId, data) =>
  api.post(`/customers/${customerId}/interactions`, data);
export const updateInteraction = (customerId, id, data) =>
  api.put(`/customers/${customerId}/interactions/${id}`, data);
export const deleteInteraction = (customerId, id) =>
  api.delete(`/customers/${customerId}/interactions/${id}`);

// User Management
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const resetUserPassword = (id, data) =>
  api.post(`/users/${id}/reset-password`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Industries
export const getIndustries = () => api.get('/industries');
export const createIndustry = (data) => api.post('/industries', data);
export const updateIndustry = (id, data) => api.put(`/industries/${id}`, data);
export const deleteIndustry = (id) => api.delete(`/industries/${id}`);

// Industry Groups
export const getIndustryGroups = () => api.get('/industry-groups');
export const getIndustryGroup = (id) => api.get(`/industry-groups/${id}`);
export const createIndustryGroup = (data) => api.post('/industry-groups', data);
export const updateIndustryGroup = (id, data) => api.put(`/industry-groups/${id}`, data);
export const deleteIndustryGroup = (id) => api.delete(`/industry-groups/${id}`);
export const assignIndustryToGroup = (industryId, groupId) =>
  api.post('/industry-groups/assign', { industryId, groupId });
export const aiResolveIndustryGroup = (industryId) =>
  api.post(`/industry-groups/ai/resolve/${industryId}`);
export const aiSweepStart = ({ provider, includeGrouped, batchLimit } = {}) =>
  api.post('/industry-groups/ai/sweep/start', { provider, includeGrouped, batchLimit });
export const aiSweepStatus = (jobId) =>
  api.get('/industry-groups/ai/sweep/status', { params: jobId ? { jobId } : {} });
export const aiSweepCancel = (jobId) =>
  api.post('/industry-groups/ai/sweep/cancel', null, { params: { jobId } });
export const aiBulkApplyIndustryGroups = (items) =>
  api.post('/industry-groups/ai/bulk-apply', { items });

// Profiling Templates
export const getProfilingTemplates = () => api.get('/profiling-templates');
export const getProfilingTemplate = (id) => api.get(`/profiling-templates/${id}`);
export const getDefaultProfilingTemplate = () => api.get('/profiling-templates/default');
export const createProfilingTemplate = (data) =>
  api.post('/profiling-templates', data);
export const updateProfilingTemplate = (id, data) =>
  api.put(`/profiling-templates/${id}`, data);
export const deleteProfilingTemplate = (id) =>
  api.delete(`/profiling-templates/${id}`);

// Product Briefs (Marketing Brain)
export const getProductBriefs = () => api.get('/product-briefs');
export const getProductBrief = (id) => api.get(`/product-briefs/${id}`);
export const getActiveProductBrief = () => api.get('/product-briefs/active');
export const createProductBrief = (data) => api.post('/product-briefs', data);
export const updateProductBrief = (id, data) =>
  api.put(`/product-briefs/${id}`, data);
export const deleteProductBrief = (id) => api.delete(`/product-briefs/${id}`);
export const setActiveProductBrief = (id) =>
  api.post(`/product-briefs/${id}/set-active`);

// Signals
export const getSignals = () => api.get('/signals');
export const getSignal = (id) => api.get(`/signals/${id}`);
export const getActiveSignals = () => api.get('/signals/active');
export const getSignalsByCategory = (category) =>
  api.get(`/signals/category/${category}`);
export const createSignal = (data) => api.post('/signals', data);
export const updateSignal = (id, data) => api.put(`/signals/${id}`, data);
export const deleteSignal = (id) => api.delete(`/signals/${id}`);

// AI Prompt Templates
export const getAiPromptTemplates = () => api.get('/ai-prompt-templates');
export const getAiPromptTemplatesByCategory = (category) =>
  api.get(`/ai-prompt-templates/category/${category}`);
export const getAiPromptTemplate = (id) => api.get(`/ai-prompt-templates/${id}`);
export const createAiPromptTemplate = (data) => api.post('/ai-prompt-templates', data);
export const updateAiPromptTemplate = (id, data) => api.put(`/ai-prompt-templates/${id}`, data);
export const deleteAiPromptTemplate = (id) => api.delete(`/ai-prompt-templates/${id}`);

// Leads
export const getLeads = (params) => api.get('/leads', { params });
export const getLeadSourceDetails = () => api.get('/leads/source-details');
export const getLead = (id) => api.get(`/leads/${id}`);
export const createLead = (data) => api.post('/leads', data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const importLeads = (data) => api.post('/leads/import', data);
export const checkLeadDomains = (batchSize) =>
  api.post('/leads/check-domains', null, { params: batchSize ? { batchSize } : {} });
export const previewDomainCheck = (filter) => api.post('/leads/domain-check/preview', filter);
export const startDomainCheckRun = (filter) => api.post('/leads/domain-check/runs', filter);
export const getDomainCheckRun = (id) => api.get(`/leads/domain-check/runs/${id}`);
export const listDomainCheckRuns = (limit = 20) =>
  api.get('/leads/domain-check/runs', { params: { limit } });

// Auto Enrichment (batch AI enrichment jobs)
export const previewAutoEnrichment = (filter) =>
  api.post('/leads/auto-enrichment/preview', filter);
export const startAutoEnrichment = (body) =>
  api.post('/leads/auto-enrichment/runs', body);
export const listAutoEnrichmentRuns = (limit = 20) =>
  api.get('/leads/auto-enrichment/runs', { params: { limit } });
export const getAutoEnrichmentRun = (id) =>
  api.get(`/leads/auto-enrichment/runs/${id}`);
export const listAutoEnrichmentItems = (id, params) =>
  api.get(`/leads/auto-enrichment/runs/${id}/items`, { params });
export const cancelAutoEnrichmentRun = (id, force = false) =>
  api.post(`/leads/auto-enrichment/runs/${id}/cancel`, null, force ? { params: { force: true } } : undefined);
export const resumeAutoEnrichmentRun = (id, force = false) =>
  api.post(`/leads/auto-enrichment/runs/${id}/resume`, null, force ? { params: { force: true } } : undefined);
export const recheckLeadVerdicts = (body) =>
  api.post('/leads/auto-enrichment/recheck-verdicts', body || {});
export const updateAutoEnrichmentRun = (id, body) =>
  api.patch(`/leads/auto-enrichment/runs/${id}`, body || {});
export const deleteAutoEnrichmentRun = (id) =>
  api.delete(`/leads/auto-enrichment/runs/${id}`);

// Contact Discovery (batch AI contact discovery jobs)
export const previewContactDiscovery = (body) =>
  api.post('/contact-discovery/preview', body);
export const startContactDiscovery = (body) =>
  api.post('/contact-discovery/runs', body);
export const listContactDiscoveryRuns = (limit = 20) =>
  api.get('/contact-discovery/runs', { params: { limit } });
export const getContactDiscoveryRun = (id) =>
  api.get(`/contact-discovery/runs/${id}`);
export const listContactDiscoveryItems = (id, params) =>
  api.get(`/contact-discovery/runs/${id}/items`, { params });
export const cancelContactDiscoveryRun = (id, force = false) =>
  api.post(`/contact-discovery/runs/${id}/cancel`, null, force ? { params: { force: true } } : undefined);
export const resumeContactDiscoveryRun = (id, force = false) =>
  api.post(`/contact-discovery/runs/${id}/resume`, null, force ? { params: { force: true } } : undefined);
export const updateContactDiscoveryRun = (id, body) =>
  api.patch(`/contact-discovery/runs/${id}`, body || {});
export const deleteContactDiscoveryRun = (id) =>
  api.delete(`/contact-discovery/runs/${id}`);

// Auto Email Outreach (batch outreach jobs)
export const previewAutoOutreach = (body) =>
  api.post('/auto-outreach/preview', body);
export const startAutoOutreach = (body) =>
  api.post('/auto-outreach/runs', body);
export const listAutoOutreachRuns = (limit = 20) =>
  api.get('/auto-outreach/runs', { params: { limit } });
export const getAutoOutreachRun = (id) =>
  api.get(`/auto-outreach/runs/${id}`);
export const listAutoOutreachItems = (id, params) =>
  api.get(`/auto-outreach/runs/${id}/items`, { params });
export const cancelAutoOutreachRun = (id, force = false) =>
  api.post(`/auto-outreach/runs/${id}/cancel`, null, force ? { params: { force: true } } : undefined);
export const resumeAutoOutreachRun = (id, force = false) =>
  api.post(`/auto-outreach/runs/${id}/resume`, null, force ? { params: { force: true } } : undefined);
export const updateAutoOutreachRun = (id, body) =>
  api.patch(`/auto-outreach/runs/${id}`, body || {});
export const deleteAutoOutreachRun = (id) =>
  api.delete(`/auto-outreach/runs/${id}`);
export const approveAutoOutreachItem = (jobId, itemId, body) =>
  api.post(`/auto-outreach/runs/${jobId}/items/${itemId}/approve`, body || {});
export const regenerateAutoOutreachItem = (jobId, itemId) =>
  api.post(`/auto-outreach/runs/${jobId}/items/${itemId}/regenerate`);
export const sendAllReadyForJob = (jobId, includeNeedsReview = false) =>
  api.post(`/auto-outreach/runs/${jobId}/send-all-ready`, null, { params: { includeNeedsReview } });
export const sendAllReadyGlobal = (includeNeedsReview = false, mineOnly = false) =>
  api.post('/interaction/email/queue/send-all-ready', null, { params: { includeNeedsReview, mineOnly } });

// Inbox + Outreach Timeline
export const listInbox = (params) => api.get('/outreach/inbox', { params });
export const getInboxCounts = () => api.get('/outreach/inbox/counts');
export const getLeadOutreachTimeline = (leadId) => api.get(`/leads/${leadId}/outreach-timeline`);

export const convertLead = (id, data) =>
  api.post(`/leads/${id}/convert`, data);

// Lead Contacts
export const getLeadContacts = (leadId) =>
  api.get(`/leads/${leadId}/contacts`);
export const createLeadContact = (leadId, data) =>
  api.post(`/leads/${leadId}/contacts`, data);
export const updateLeadContact = (leadId, id, data) =>
  api.put(`/leads/${leadId}/contacts/${id}`, data);
export const deleteLeadContact = (leadId, id) =>
  api.delete(`/leads/${leadId}/contacts/${id}`);

// Mailing list opt-out (Lead + Contact)
export const unsubscribeLead = (leadId, reason) =>
  api.post(`/leads/${leadId}/unsubscribe`, { reason: reason || null });
export const resubscribeLead = (leadId) =>
  api.post(`/leads/${leadId}/resubscribe`);
export const unsubscribeLeadContact = (leadId, contactId, reason) =>
  api.post(`/leads/${leadId}/contacts/${contactId}/unsubscribe`, { reason: reason || null });
export const resubscribeLeadContact = (leadId, contactId) =>
  api.post(`/leads/${leadId}/contacts/${contactId}/resubscribe`);
export const markLeadContactInvalid = (leadId, contactId, reason) =>
  api.post(`/leads/${leadId}/contacts/${contactId}/mark-invalid`, { reason: reason || null });
export const unmarkLeadContactInvalid = (leadId, contactId) =>
  api.post(`/leads/${leadId}/contacts/${contactId}/unmark-invalid`);

// Saved Searches
export const getSavedSearches = () => api.get('/saved-searches');
export const getSavedSearch = (id) => api.get(`/saved-searches/${id}`);
export const createSavedSearch = (data) => api.post('/saved-searches', data);
export const updateSavedSearch = (id, data) =>
  api.put(`/saved-searches/${id}`, data);
export const deleteSavedSearch = (id) => api.delete(`/saved-searches/${id}`);

// Playbooks
export const getPlaybooks = () => api.get('/playbooks');
export const getPlaybook = (id) => api.get(`/playbooks/${id}`);
export const createPlaybook = (data) => api.post('/playbooks', data);
export const updatePlaybook = (id, data) => api.put(`/playbooks/${id}`, data);
export const deletePlaybook = (id) => api.delete(`/playbooks/${id}`);
export const startPlaybook = (id) => api.post(`/playbooks/${id}/run`);
export const getPlaybookRuns = (id) => api.get(`/playbooks/${id}/runs`);
export const getPlaybookRun = (runId) => api.get(`/playbooks/runs/${runId}`);

// AI
export const getAiProviders = () => api.get('/ai/providers');
export const aiSearch = (data) => api.post('/ai/search', data);
export const aiSearchPlan = (data) => api.post('/ai/search/plan', data);
export const aiSearchExecute = (data) => api.post('/ai/search/execute', data);
export const submitAiFeedback = (data) => api.post('/ai/feedback', data);
export const aiEnrichLead = (leadId, provider, signal) =>
  api.post(`/ai/enrich-lead/${leadId}${provider ? `?provider=${provider}` : ''}`, null, { signal });
export const updateLeadAiInsights = (leadId, markdown) =>
  api.put(`/leads/${leadId}/ai-insights`, { markdown });
export const generateLeadOutreachHint = (leadId, provider) =>
  api.post(`/ai/lead/${leadId}/outreach-hint${provider ? `?provider=${provider}` : ''}`);
export const submitEnrichmentReview = (data) =>
  api.post('/ai/enrichment-review', data);
export const aiSuggestNextSteps = (data) =>
  api.post('/ai/suggest-next-steps', data);

// ICP Profiles
export const getIcpProfiles = () => api.get('/icp-profiles');
export const getIcpProfile = (id) => api.get(`/icp-profiles/${id}`);
export const getDefaultIcpProfile = () => api.get('/icp-profiles/default');
export const createIcpProfile = (data) => api.post('/icp-profiles', data);
export const updateIcpProfile = (id, data) =>
  api.put(`/icp-profiles/${id}`, data);
export const deleteIcpProfile = (id) => api.delete(`/icp-profiles/${id}`);
export const scoreLead = (leadId, icpProfileId) =>
  api.post(`/icp-profiles/score-lead/${leadId}${icpProfileId ? `?icpProfileId=${icpProfileId}` : ''}`);
export const bulkScoreLeads = (data) =>
  api.post('/icp-profiles/bulk-score', data);

// Lead Interactions
export const getLeadInteractions = (leadId) =>
  api.get(`/leads/${leadId}/interactions`);
export const createLeadInteraction = (leadId, data) =>
  api.post(`/leads/${leadId}/interactions`, data);
export const updateLeadInteraction = (leadId, id, data) =>
  api.put(`/leads/${leadId}/interactions/${id}`, data);
export const deleteLeadInteraction = (leadId, id) =>
  api.delete(`/leads/${leadId}/interactions/${id}`);

// Due Follow-ups
export const getDueFollowUps = () => api.get('/interactions/due-followups');
export const completeFollowUp = (id, type) =>
  api.post(`/interactions/${id}/complete-followup?type=${type || 'lead'}`);

// Campaigns
export const getCampaigns = () => api.get('/campaigns');
export const getCampaign = (id) => api.get(`/campaigns/${id}`);
export const createCampaign = (data) => api.post('/campaigns', data);
export const updateCampaign = (id, data) => api.put(`/campaigns/${id}`, data);
export const deleteCampaign = (id) => api.delete(`/campaigns/${id}`);
export const updateCampaignStatus = (id, data) =>
  api.put(`/campaigns/${id}/status`, data);
export const addCampaignRecipients = (id, data) =>
  api.post(`/campaigns/${id}/recipients`, data);
export const removeCampaignRecipient = (campaignId, recipientId) =>
  api.delete(`/campaigns/${campaignId}/recipients/${recipientId}`);
export const generateCampaignContent = (id, data) =>
  api.post(`/campaigns/${id}/generate-content`, data);
export const getCampaignAnalytics = (id) =>
  api.get(`/campaigns/${id}/analytics`);

// Workspace
export const getWorkspaceSummary = () => api.get('/workspace/summary');
export const getWorkspaceQueue = (queueName, page = 1, pageSize = 20) =>
  api.get(`/workspace/queue/${queueName}?page=${page}&pageSize=${pageSize}`);
export const getWorkspaceQueueRegistry = () => api.get('/workspace/queues/registry');
export const getAiFeedbackSummary = (from, to) => {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  return api.get(`/workspace/ai-feedback-summary?${params}`);
};
export const getAiUsageSummary = (from, to) => {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  return api.get(`/workspace/ai-usage-summary?${params}`);
};

// AI Settings
export const getAiSettings = () => api.get('/ai-settings');
export const updateAiSettings = (data) => api.put('/ai-settings', data);
export const testAiProvider = (data) => api.post('/ai-settings/test', data);
export const testHunterIo = () => api.post('/ai-settings/test-hunterio');

// AI Contact Discovery
export const getContactProviders = () => api.get('/ai/contact-providers');
export const findContacts = (data, config) => api.post('/ai/find-contacts', data, config);
// data: { leadId, providerNames: string[] }

// ─── Phase 4: Lead Interactions Module ───

// Interaction Templates
export const getInteractionTemplates = (params) =>
  api.get('/interaction/templates', { params });
export const getInteractionTemplate = (id) =>
  api.get(`/interaction/templates/${id}`);
export const createInteractionTemplate = (data) =>
  api.post('/interaction/templates', data);
export const updateInteractionTemplate = (id, data) =>
  api.put(`/interaction/templates/${id}`, data);
export const deleteInteractionTemplate = (id) =>
  api.delete(`/interaction/templates/${id}`);
export const createTemplateVersion = (id, data) =>
  api.post(`/interaction/templates/${id}/new-version`, data);
export const renderTemplate = (data) =>
  api.post('/interaction/templates/render', data);

// Email Composition
export const previewEmail = (data) =>
  api.post('/interaction/email/preview', data);
export const improveEmail = (data, signal) =>
  api.post('/interaction/email/improve', data, { signal });
export const autoImproveEmail = (data, signal) =>
  api.post('/interaction/email/auto-improve', data, { signal });
export const translateEmailPreview = (data, signal) =>
  api.post('/interaction/email/translate-preview', data, { signal });
export const personalizeEmail = (data, signal) =>
  api.post('/interaction/email/personalize', data, { signal });
export const runEmailChain = (data, signal) =>
  api.post('/interaction/email/run-chain', data, { signal });
export const sendEmail = (data) =>
  api.post('/interaction/email/send', data);

// Email Queue (Drafts / Scheduled)
export const saveEmailDraft = (data) =>
  api.post('/interaction/email/draft', data);
export const scheduleEmail = (data) =>
  api.post('/interaction/email/schedule', data);
export const listEmailQueue = (status, mineOnly = false, page = 1, pageSize = 25, stage = null, scheduledForFrom = null, scheduledForTo = null) =>
  api.get('/interaction/email/queue', { params: { status, mineOnly, page, pageSize, ...(stage ? { stage } : {}), ...(scheduledForFrom ? { scheduledForFrom } : {}), ...(scheduledForTo ? { scheduledForTo } : {}) } });
export const getEmailQueueItem = (id) =>
  api.get(`/interaction/email/queue/${id}`);
export const cancelEmailQueueItem = (id) =>
  api.delete(`/interaction/email/queue/${id}`);
export const sendQueuedEmailNow = (id) =>
  api.post(`/interaction/email/queue/${id}/send-now`);
export const getEmailQueueStatusCounts = () =>
  api.get('/interaction/email/queue/status-counts');
export const listEmailQueueMonitor = (status, take = 50) =>
  api.get('/interaction/email/queue/monitor', { params: { status, take } });
export const pauseEmailWorker = () =>
  api.post('/interaction/email/queue/worker/pause');
export const resumeEmailWorker = () =>
  api.post('/interaction/email/queue/worker/resume');
export const getEmailWorkerStatus = () =>
  api.get('/interaction/email/queue/worker/status');
export const getImapPollerStatus = () =>
  api.get('/interaction/inbound/imap/status');
export const pauseImapPoller = () =>
  api.post('/interaction/inbound/imap/pause');
export const resumeImapPoller = () =>
  api.post('/interaction/inbound/imap/resume');

// Per-user IMAP settings (stored as jsonb on Users.ImapConfigJson).
export const getMyImapSettings = () => api.get('/auth/me/imap');
export const updateMyImapSettings = (data) => api.put('/auth/me/imap', data);
export const testMyImapSettings = (data) => api.post('/auth/me/imap/test', data);
export const refreshImapInbox = (params) => api.post('/auth/me/imap/refresh', null, { params });
export const reclassifyInboxItem = (id, provider) =>
  api.post(`/outreach/inbox/${id}/reclassify`, null, { params: provider ? { provider } : undefined });
export const getInboxThread = (id) => api.get(`/outreach/inbox/${id}/thread`);
export const assignInboundItem = (id, body) => api.post(`/outreach/inbox/${id}/assign`, body);
export const getLeadContactsLight = (leadId) => api.get(`/leads/${leadId}/contacts-light`);

// LinkedIn Manual-Assist
export const generateLinkedInMessage = (data) =>
  api.post('/interaction/linkedin/generate-message', data);
export const analyzeLinkedInReply = (data) =>
  api.post('/interaction/linkedin/analyze-reply', data);
export const logSentLinkedInMessage = (data) =>
  api.post('/interaction/linkedin/log-sent', data);

// All Contacts (cross-lead)
export const listAllContacts = (params) => api.get('/contacts', { params });
export const bulkOutreachContacts = (data) => api.post('/contacts/bulk-outreach', data);
export const getContactInsights = (id) => api.get(`/contacts/${id}/insights`);
export const setContactLinkedInConnected = (id, connected) =>
  api.post(`/contacts/${id}/linkedin-connected`, { connected });
export const setContactLinkedInUrl = (id, url) =>
  api.post(`/contacts/${id}/linkedin-url`, { url });

// Inbound Email
export const processInboundWebhook = (data) =>
  api.post('/interaction/inbound/email/webhook', data);
export const processInboundManual = (data) =>
  api.post('/interaction/inbound/manual-entry', data);

// Interaction Timeline
export const getInteractionTimeline = (leadId, params) =>
  api.get(`/leads/${leadId}/interactions/timeline`, { params });
export const getInteractionDetail = (id) =>
  api.get(`/interactions/${id}`);
export const addInteractionNote = (id, data) =>
  api.post(`/interactions/${id}/note`, data);
export const getRecommendedContacts = (leadId) =>
  api.get(`/leads/${leadId}/interactions/recommended-contacts`);

// Global Activities (cross-lead interaction list)
export const getActivities = (params) =>
  api.get('/interactions', {
    params,
    paramsSerializer: { indexes: null },
  });
export const getWorkspaceMembers = () => api.get('/workspace/members');

// Email Settings
export const getEmailSettings = () => api.get('/email-settings');
export const updateEmailSettings = (configs) =>
  api.put('/email-settings', Array.isArray(configs) ? configs : [configs]);
export const testEmailConnection = (providerName) =>
  api.post('/email-settings/test', null, { params: { providerName } });

// Lead Follow-ups
export const getLeadFollowUps = (leadId) =>
  api.get(`/leads/${leadId}/interactions/follow-ups`);

// System Email Templates (transactional — demo/exchange)
export const getSystemEmailTemplates = () =>
  api.get('/system-email-templates');
export const getSystemEmailTemplate = (key) =>
  api.get(`/system-email-templates/${encodeURIComponent(key)}`);
export const updateSystemEmailTemplate = (key, data) =>
  api.put(`/system-email-templates/${encodeURIComponent(key)}`, data);
export const previewSystemEmailTemplate = (key, toEmail) =>
  api.post(`/system-email-templates/${encodeURIComponent(key)}/preview`, { toEmail });

// Appointment Types (demo / exchange / future)
export const getAppointmentTypes = () =>
  api.get('/demo-bookings/types');
export const getAppointmentType = (key) =>
  api.get(`/demo-bookings/types/${encodeURIComponent(key)}`);
export const updateAppointmentType = (key, data) =>
  api.put(`/demo-bookings/types/${encodeURIComponent(key)}`, data);

// Demo / Exchange Bookings (admin)
export const getDemoBookings = (params) =>
  api.get('/demo-bookings/admin', { params });
export const reassignDemoBooking = (id, userId) =>
  api.post(`/demo-bookings/${id}/reassign`, { userId });
export const cancelDemoBookingAdmin = (id, reason) =>
  api.post(`/demo-bookings/${id}/admin-cancel`, { reason });
export const resendDemoBookingConfirmation = (id) =>
  api.post(`/demo-bookings/${id}/resend-confirmation`);

// Meetings & Calendar
export const getMeetings = (params) => api.get('/meetings', { params });
export const getMeeting = (id) => api.get(`/meetings/${id}`);
export const getLeadMeetings = (leadId) =>
  api.get(`/meetings/lead/${leadId}`);
export const createMeeting = (data) => api.post('/meetings', data);
export const updateMeeting = (id, data) => api.put(`/meetings/${id}`, data);
export const deleteMeeting = (id) => api.delete(`/meetings/${id}`);
export const cancelMeeting = (id) => api.post(`/meetings/${id}/cancel`);

// Lead-level note (creates an internal note interaction; does NOT auto-transition status)
export const addLeadNote = (leadId, note) =>
  api.post(`/leads/${leadId}/notes`, { note });

// Lead Tasks
export const getLeadTasks = (leadId, params) =>
  api.get(`/leads/${leadId}/tasks`, { params });
export const createLeadTask = (leadId, data) =>
  api.post(`/leads/${leadId}/tasks`, data);
export const getTask = (id) => api.get(`/tasks/${id}`);
export const updateTask = (id, data) => api.patch(`/tasks/${id}`, data);
export const completeTask = (id, data) => api.post(`/tasks/${id}/complete`, data || {});
export const reopenTask = (id) => api.post(`/tasks/${id}/reopen`);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
export const getMyTasks = (params) => api.get('/tasks/me', { params });
export const getMyTasksPaged = ({ scope = 'open', page = 1, pageSize = 20 } = {}) =>
  api.get('/tasks/me/paged', { params: { scope, page, pageSize } });

// Marketing-site analytics (admin-only summary; ingest is server-to-server)
export const getAnalyticsSummary = (params) =>
  api.get('/analytics/summary', { params });
export const getAnalyticsEvents = (params) =>
  api.get('/analytics/events', { params });
export const getLeadAnalyticsEvents = (leadId, params) =>
  api.get(`/analytics/lead/${leadId}/events`, { params });
export const getAnalyticsChannelMix = (params) =>
  api.get('/analytics/channel-mix', { params });
export const getAnalyticsGeo = (params) =>
  api.get('/analytics/geo', { params });
export const getAnalyticsCampaignCountry = (params) =>
  api.get('/analytics/campaign-country', { params });

// Personal calendar blockers (busy / tentative / free / OOO)
export const getCalendarBlockers = (params) =>
  api.get('/calendar-blockers', { params });
export const getCalendarBlocker = (id) =>
  api.get(`/calendar-blockers/${id}`);
export const createCalendarBlocker = (data) =>
  api.post('/calendar-blockers', data);
export const updateCalendarBlocker = (id, data) =>
  api.put(`/calendar-blockers/${id}`, data);
export const deleteCalendarBlocker = (id) =>
  api.delete(`/calendar-blockers/${id}`);
export const skipCalendarBlockerOccurrence = (id, occurrenceStartUtc) =>
  api.post(`/calendar-blockers/${id}/skip`, { occurrenceStartUtc });

// Logs
export const queryLogs = (params) => api.get('/logs', { params });
export const getLogLevels = () => api.get('/logs/levels');

// Lead Signals (AI-extracted "Signals to Track")
export const getLeadSignals = (leadId) => api.get(`/leads/${leadId}/signals`);
export const createLeadSignal = (leadId, data) => api.post(`/leads/${leadId}/signals`, data);
export const updateLeadSignalStatus = (leadId, id, status) =>
  api.patch(`/leads/${leadId}/signals/${id}/status`, { status });
export const deleteLeadSignal = (leadId, id) => api.delete(`/leads/${leadId}/signals/${id}`);

// Lead Segmentations (named dynamic segments by Approach / Industry / etc.)
export const getLeadSegmentations = (params) => api.get('/lead-segmentations', { params });
export const getLeadSegmentation = (id) => api.get(`/lead-segmentations/${id}`);
export const createLeadSegmentation = (data) => api.post('/lead-segmentations', data);
export const updateLeadSegmentation = (id, data) => api.put(`/lead-segmentations/${id}`, data);
export const deleteLeadSegmentation = (id) => api.delete(`/lead-segmentations/${id}`);
export const getLeadSegmentationMembersList = (id) =>
  api.get(`/lead-segmentations/${id}/members`);

export const getLeadSegmentationMembers = (leadId) => api.get(`/leads/${leadId}/segmentations`);
export const assignLeadSegmentation = (leadId, data) =>
  api.post(`/leads/${leadId}/segmentations`, data);
export const removeLeadSegmentationMember = (leadId, memberId) =>
  api.delete(`/leads/${leadId}/segmentations/${memberId}`);

// Segmentation rules (Phase 1 MVP)
export const getSegmentationFields = () => api.get('/segmentation/fields');
export const validateSegmentationRule = (data) => api.post('/segmentation/validate', data);
export const previewSegmentationRule = (data) => api.post('/segmentation/preview', data);
export const previewLeadSegmentation = (id, sampleSize = 10) =>
  api.post(`/lead-segmentations/${id}/preview`, null, { params: { sampleSize } });
export const refreshLeadSegmentation = (id) =>
  api.post(`/lead-segmentations/${id}/refresh`);

// ─── Reports ───
const reportParams = ({ from, to, window, ...rest } = {}) => {
  const p = {};
  if (from) p.from = from;
  if (to) p.to = to;
  if (window) p.window = window;
  for (const [k, v] of Object.entries(rest)) {
    if (v == null) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    p[k] = v;
  }
  return p;
};

// Executive Overview
export const getOverviewReport = (filters) => api.get('/reports/overview', { params: reportParams(filters) });

// Marketing domain reports (M1–M7)
export const getMarketingVisitFunnelReport = (filters) => api.get('/reports/marketing/visit-funnel', { params: reportParams(filters) });
export const getMarketingUtmRoiReport = (filters) => api.get('/reports/marketing/utm-roi', { params: reportParams(filters) });
export const getMarketingPagePathsReport = (filters) => api.get('/reports/marketing/page-paths', { params: reportParams(filters) });
export const getMarketingGeoReport = (filters) => api.get('/reports/marketing/geo-conversion', { params: reportParams(filters) });
export const getMarketingChannelTrendReport = (filters) => api.get('/reports/marketing/channel-trend', { params: reportParams(filters) });
export const getMarketingDemoFunnelReport = (filters) => api.get('/reports/marketing/demo-funnel', { params: reportParams(filters) });
export const getMarketingIdentificationBridgeReport = (filters) => api.get('/reports/marketing/identification-bridge', { params: reportParams(filters) });

// AI Ops domain reports (A1–A7)
export const getAiEnrichmentJobsReport = (filters) => api.get('/reports/ai-ops/enrichment-jobs', { params: reportParams(filters) });
export const getAiProvidersReport = (filters) => api.get('/reports/ai-ops/providers', { params: reportParams(filters) });
export const getAiFeedbackReport = (filters) => api.get('/reports/ai-ops/feedback', { params: reportParams(filters) });
export const getAiPromptsReport = (filters) => api.get('/reports/ai-ops/prompts', { params: reportParams(filters) });
export const getAiFreshnessReport = (filters) => api.get('/reports/ai-ops/freshness', { params: reportParams(filters) });
export const getAiDomainHealthReport = () => api.get('/reports/ai-ops/domain-health');
export const getAiCoverageReport = () => api.get('/reports/ai-ops/coverage');

// Segmentation domain reports (S1–S7)
export const getSegmentationRosterReport = (filters) => api.get('/reports/segmentations/roster', { params: reportParams(filters) });
export const getSegmentationPerformanceReport = (filters) => api.get('/reports/segmentations/performance', { params: reportParams(filters) });
export const getSegmentationOverlapReport = (filters) => api.get('/reports/segmentations/overlap', { params: reportParams(filters) });
export const getSegmentationEvaluationsReport = (id, filters) => api.get(`/reports/segmentations/${id}/evaluations`, { params: reportParams(filters) });
export const getSegmentationSourceMixReport = (id) => api.get(`/reports/segmentations/${id}/source-mix`);
export const getSegmentationLifecycleReport = (filters) => api.get('/reports/segmentations/lifecycle', { params: reportParams(filters) });
export const getSegmentationCoverageReport = () => api.get('/reports/segmentations/coverage');

// Outreach domain reports (O1–O10)
export const getOutreachCampaignsReport = (filters) => api.get('/reports/outreach/campaigns', { params: reportParams(filters) });
export const getOutreachFunnelReport = (filters) => api.get('/reports/outreach/funnel', { params: reportParams(filters) });
export const getOutreachChannelsReport = (filters) => api.get('/reports/outreach/channels', { params: reportParams(filters) });
export const getOutreachStagesReport = (filters) => api.get('/reports/outreach/stages', { params: reportParams(filters) });
export const getOutreachSentimentReport = (filters) => api.get('/reports/outreach/sentiment', { params: reportParams(filters) });
export const getOutreachFollowUpsReport = (filters) => api.get('/reports/outreach/follow-ups', { params: reportParams(filters) });
export const getOutreachTimingReport = (filters) => api.get('/reports/outreach/timing-heatmap', { params: reportParams(filters) });
export const getOutreachPersonaToneReport = (filters) => api.get('/reports/outreach/persona-tone', { params: reportParams(filters) });
export const getOutreachFailuresReport = (filters) => api.get('/reports/outreach/failures', { params: reportParams(filters) });
export const getOutreachRepsReport = (filters) => api.get('/reports/outreach/reps', { params: reportParams(filters) });

// Industry domain reports (I1–I7)
export const getIndustryDistributionReport = (filters) => api.get('/reports/industries/distribution', { params: reportParams(filters) });
export const getIndustryGroupHealthReport = (filters) => api.get('/reports/industries/group-health', { params: reportParams(filters) });
export const getIndustryDrilldownReport = (filters) => api.get('/reports/industries/drilldown', { params: reportParams(filters) });
export const getIndustryGapsReport = (filters) => api.get('/reports/industries/gaps', { params: reportParams(filters) });
export const getIndustryGroupDriftReport = (filters) => api.get('/reports/industries/group-drift', { params: reportParams(filters) });
export const getIndustryAiFitMatrixReport = (filters) => api.get('/reports/industries/ai-fit-matrix', { params: reportParams(filters) });
export const getIndustryUncategorizedReport = (filters) => api.get('/reports/industries/uncategorized', { params: reportParams(filters) });

// Leads domain reports (L1–L8)
export const getLeadsFunnelReport = (filters) => api.get('/reports/leads/funnel', { params: reportParams(filters) });
export const getLeadsSourcesReport = (filters) => api.get('/reports/leads/sources', { params: reportParams(filters) });
export const getLeadsAiVerdictsReport = (filters) => api.get('/reports/leads/ai-verdicts', { params: reportParams(filters) });
export const getLeadsScoringReport = (filters) => api.get('/reports/leads/scoring', { params: reportParams(filters) });
export const getLeadsGeoReport = (filters) => api.get('/reports/leads/geo', { params: reportParams(filters) });
export const getLeadsAgingReport = (filters) => api.get('/reports/leads/aging', { params: reportParams(filters) });
export const getLeadsAssignmentReport = (filters) => api.get('/reports/leads/assignment', { params: reportParams(filters) });
export const getLeadsProfileCoverageReport = (filters) => api.get('/reports/leads/profile-coverage', { params: reportParams(filters) });

// CSV export — caller downloads via fetch with auth header to honor token.
export const fetchReportCsv = async (path, filters) => {
  const url = `${API_BASE}/api${path}`;
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const params = reportParams(filters);
  const u = new URL(url);
  for (const [k, v] of Object.entries(params)) {
    if (Array.isArray(v)) v.forEach((x) => u.searchParams.append(k, x));
    else u.searchParams.append(k, v);
  }
  const res = await fetch(u.toString(), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
};

export default api;
